import { useMemo } from 'react';
import { normalizeTeamNameForComparison } from '../utils/teamUtils';
import { useConfig } from '../context/ConfigContext';
import { WinnerWithMetadata, WinnerSource } from '../types/types';

export interface PlayerScoreInfo {
  name: string;
  currentScore: number;
  scenarioScore: number;
}

// Game round lookup: maps game index (0-62) to round number (0-5)
const GAME_ROUNDS: number[] = (() => {
  const rounds: number[] = [];
  let gamesInRound = 32;
  let currentRound = 0;
  while (gamesInRound > 0) {
    for (let i = 0; i < gamesInRound; i++) {
      rounds.push(currentRound);
    }
    gamesInRound = Math.floor(gamesInRound / 2);
    currentRound++;
  }
  return rounds;
})();

/** Parse player picks text into a map of player name -> pick array */
export function parsePlayerPicks(playerPicks: string): Record<string, string[]> {
  if (!playerPicks) return {};

  const players: Record<string, string[]> = {};
  const lines = playerPicks.trim().split('\n');

  for (const line of lines) {
    let match = line.match(/^"([^"]+)"\s*=\s*(.+)$/);
    if (!match) {
      match = line.match(/^(\w+)\s*=\s*(.+)$/);
    }
    if (match) {
      players[match[1]] = match[2].split(',').map(pick => pick.trim());
    }
  }

  return players;
}

/** Split WinnerWithMetadata into official-only and all-winners arrays */
export function splitWinners(winnersWithMetadata: WinnerWithMetadata[]) {
  const officialWinners: string[] = [];
  const scenarioWinners: string[] = [];

  winnersWithMetadata.forEach((winner, index) => {
    if (winner && winner.team) {
      if (winner.source === WinnerSource.OFFICIAL) {
        officialWinners[index] = winner.team;
      }
      scenarioWinners[index] = winner.team;
    }
  });

  return { officialWinners, scenarioWinners };
}

/** Compute dense rank map with ties (by scenarioScore desc, then currentScore desc) */
export function computeRankMap(scores: PlayerScoreInfo[]): Map<string, number> {
  const map = new Map<string, number>();
  if (scores.length === 0) return map;

  let rank = 1;
  map.set(scores[0].name, 1);
  for (let i = 1; i < scores.length; i++) {
    if (
      scores[i].scenarioScore < scores[i - 1].scenarioScore ||
      (scores[i].scenarioScore === scores[i - 1].scenarioScore &&
        scores[i].currentScore < scores[i - 1].currentScore)
    ) {
      rank = i + 1;
    }
    map.set(scores[i].name, rank);
  }
  return map;
}

/**
 * Hook that calculates player scores from picks and winners.
 * Replaces the duplicated score calculation in GeneratedStandings and FloatingStandings.
 */
export function usePlayerScores(
  playerPicks: string,
  winnersWithMetadata: WinnerWithMetadata[]
): {
  playerScores: PlayerScoreInfo[];
  rankMap: Map<string, number>;
  officialWinners: string[];
  scenarioWinners: string[];
} {
  const { currentPointValues } = useConfig();

  const parsedPicks = useMemo(() => parsePlayerPicks(playerPicks), [playerPicks]);

  const { officialWinners, scenarioWinners } = useMemo(
    () => splitWinners(winnersWithMetadata),
    [winnersWithMetadata]
  );

  const playerScores = useMemo(() => {
    if (Object.keys(parsedPicks).length === 0) return [];

    const scores: PlayerScoreInfo[] = [];

    for (const [playerName, picks] of Object.entries(parsedPicks)) {
      let currentScore = 0;
      let scenarioScore = 0;

      for (let gameIndex = 0; gameIndex < Math.min(63, picks.length); gameIndex++) {
        const normalizedPick = normalizeTeamNameForComparison(picks[gameIndex]);
        const round = GAME_ROUNDS[gameIndex];
        const points = round < currentPointValues.length ? currentPointValues[round] : 0;

        if (gameIndex < officialWinners.length && officialWinners[gameIndex]) {
          if (normalizedPick === normalizeTeamNameForComparison(officialWinners[gameIndex])) {
            currentScore += points;
          }
        }

        if (gameIndex < scenarioWinners.length && scenarioWinners[gameIndex]) {
          if (normalizedPick === normalizeTeamNameForComparison(scenarioWinners[gameIndex])) {
            scenarioScore += points;
          }
        }
      }

      scores.push({ name: playerName, currentScore, scenarioScore });
    }

    return scores.sort((a, b) => {
      if (b.scenarioScore === a.scenarioScore) {
        return b.currentScore - a.currentScore;
      }
      return b.scenarioScore - a.scenarioScore;
    });
  }, [parsedPicks, officialWinners, scenarioWinners, currentPointValues]);

  const rankMap = useMemo(() => computeRankMap(playerScores), [playerScores]);

  return { playerScores, rankMap, officialWinners, scenarioWinners };
}
